// Full match feed for external matches: every round of every map, A to Z, grouped by map → round.
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { card, sectionLabel, pill, RGB, WeaponIcon } from './extUi';

export const FEED_TYPES = ['kill', 'suicide', 'bomb_plant', 'bomb_defuse_start', 'bomb_defuse', 'bomb_explode', 'round_start', 'round_end', 'pause_start', 'pause_end',
  'clutch_start', 'clutch_won', 'clutch_lost', 'ace', 'map_point', 'match_point', 'side_switch', 'halftime_start', 'knife_winner', 'knife_decision', 'map_start', 'map_end', 'round_restored', 'match_end', 'player_connect', 'player_disconnect'];

const SIDE = { CT: { color: '#5b9cf5', rgb: '91,156,245' }, T: { color: '#e8a33d', rgb: '232,163,61' } };
const REASON_ICON = { bomb_exploded: '/killfeed/explosion.svg', bomb_defused: '/killfeed/defuse.svg', time: '/killfeed/stopwatch.svg' };

const byTime = (a, b) => ((Number(a.ts_ms) || 0) - (Number(b.ts_ms) || 0)) || (a.id - b.id);

function clock(ev) {
  const s = ev.game_ts ? ev.game_ts.slice(11, 19) : new Date(ev.ts_ms).toLocaleTimeString();
  return s;
}

function Name({ p }) {
  if (!p) return <span style={{ color: 'var(--text-muted)' }}>?</span>;
  return <span style={{ color: p.side ? SIDE[p.side].color : 'var(--text-primary)', fontWeight: 600 }}>{p.name}</span>;
}

function Row({ e, teams }) {
  const { t } = useTranslation();
  const d = e.data || {};
  const row = { display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.26rem 0.5rem', fontSize: '0.8rem', flexWrap: 'wrap' };
  const time = <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.68rem', color: 'var(--text-muted)', minWidth: 54 }}>{clock(e)}</span>;
  switch (e.type) {
    case 'kill': {
      // Highlights: Zeus (taser) and knife kills on enemies
      // no highlight inside the knife round (every kill there is a knife kill)
      const zeus = !d.team_kill && !d.knife_round && /^taser$/i.test(d.weapon || '');
      const knife = !d.team_kill && !d.knife_round && /knife|bayonet/i.test(d.weapon || '');
      const hl = zeus ? RGB.cyan : knife ? RGB.violet : null;
      return (
        <div style={{ ...row, background: d.team_kill ? 'rgba(239,68,68,0.06)' : hl ? `rgba(${hl},0.1)` : 'transparent',
          borderLeft: hl ? `2px solid rgba(${hl},0.7)` : '2px solid transparent', borderRadius: hl ? 'var(--radius-sm)' : 0 }}>
          {time}<Name p={d.attacker} />
          {/* assist right after the killer, like the in-game feed, whichever team the assister is on */}
          {d.assister && <span style={{ color: 'var(--text-muted)' }}>+ <Name p={d.assister} /></span>}
          {d.flash_assister && <span style={{ color: 'var(--text-muted)' }}>+ <img src="/killfeed/flashbang.png" alt="" style={{ height: 11 }} /> <Name p={d.flash_assister} /></span>}
          {d.attacker_blind && <img src="/killfeed/shooter_blind.png" alt="blind" title={t('ext.feed.blind', 'Attacker blind')} style={{ height: 13 }} />}
          {d.in_air && <img src="/killfeed/inairkill.svg" alt="air" title={t('ext.feed.inAir', 'In the air')} style={{ height: 13 }} />}
          <WeaponIcon name={d.weapon} height={14} />
          {d.noscope && <img src="/killfeed/noscope.png" alt="noscope" style={{ height: 13 }} />}
          {d.through_smoke && <img src="/killfeed/through_smoke.png" alt="smoke" style={{ height: 13 }} />}
          {d.penetrated && <img src="/killfeed/penetration.png" alt="wallbang" style={{ height: 13 }} />}
          {d.headshot && <img src="/killfeed/headshot.png" alt="HS" style={{ height: 13 }} />}
          <Name p={d.victim} />
          {d.team_kill && <span style={pill(RGB.red)}>{t('ext.feed.teamKill', 'Team kill')}</span>}
          {zeus && <span style={pill(RGB.cyan)}>{t('ext.feed.zeusKill', 'Zeus kill')}</span>}
          {knife && <span style={pill(RGB.violet)}>{t('ext.feed.knifeKill', 'Knife kill')}</span>}
        </div>
      );
    }
    case 'suicide':
      return <div style={row}>{time}<Name p={d.player} /><span style={{ color: 'var(--text-secondary)' }}>{t('ext.feed.died', 'died')} ({d.cause})</span></div>;
    case 'bomb_plant':
      return <div style={row}>{time}<img src="/killfeed/bombplant.png" alt="" style={{ height: 13 }} /><span style={{ color: SIDE.T.color }}>{t('ext.feed.planted', '{{name}} planted the bomb on {{site}}', { name: d.player && d.player.name, site: d.site || '?' })}</span></div>;
    case 'bomb_defuse_start':
      return (
        <div style={row}>{time}<img src="/killfeed/defuse.svg" alt="" style={{ height: 13, opacity: 0.6 }} />
          <span style={{ color: SIDE.CT.color, opacity: 0.85 }}>{t('ext.feed.defuseStart', '{{name}} started defusing', { name: (d.player && d.player.name) || 'CT' })}</span>
          <span style={pill(d.with_kit ? RGB.green : RGB.gray)}>{d.with_kit ? t('ext.feed.withKit', 'with kit') : t('ext.feed.withoutKit', 'no kit')}</span>
        </div>
      );
    case 'bomb_defuse':
      return <div style={row}>{time}<img src="/killfeed/defuse.svg" alt="" style={{ height: 13 }} /><span style={{ color: SIDE.CT.color }}>{t('ext.feed.defused', '{{name}} defused the bomb', { name: (d.player && d.player.name) || 'CT' })}</span></div>;
    case 'bomb_explode':
      return <div style={row}>{time}<img src="/killfeed/explosion.svg" alt="" style={{ height: 13 }} /><span style={{ color: SIDE.T.color }}>{d.site ? t('ext.feed.explodedSite', 'C4 exploded on bombsite {{site}}', { site: d.site }) : t('ext.feed.exploded', 'The bomb exploded')}</span></div>;
    case 'clutch_start':
      return <div style={row}>{time}<span style={pill(RGB.amber)}>{t('ext.feed.clutchStart', '{{name}} is in a 1v{{vs}}', { name: d.player && d.player.name, vs: d.vs })}</span></div>;
    case 'clutch_won':
      return <div style={row}>{time}<span style={pill(RGB.green)}>{t('ext.feed.clutch', '{{name}} won a 1v{{vs}}', { name: d.player && d.player.name, vs: d.vs })}</span></div>;
    case 'clutch_lost':
      return <div style={row}>{time}<span style={pill(RGB.red)}>{t('ext.feed.clutchLost', '{{name}} lost the 1v{{vs}}', { name: d.player && d.player.name, vs: d.vs })}</span></div>;
    case 'ace':
      return <div style={row}>{time}<span style={pill(RGB.violet)}>{t('ext.feed.ace', '{{name}} ACE', { name: d.player && d.player.name })}</span></div>;
    case 'map_point':
    case 'match_point': {
      const name = teams[d.team] ? teams[d.team].name : d.team;
      const score = d.score ? ` ${d.score.team1}-${d.score.team2}` : '';
      return <div style={row}>{time}<span style={pill(e.type === 'match_point' ? RGB.red : RGB.amber)}>
        {e.type === 'match_point' ? t('ext.feed.matchPoint', 'Match point: {{team}}', { team: name }) : t('ext.feed.mapPoint', 'Map point: {{team}}', { team: name })}{score}
        {d.overtime ? ` · ${t('ext.feed.overtime', 'overtime')}` : ''}
      </span></div>;
    }
    case 'pause_start':
      return <div style={row}>{time}<span style={pill(RGB.amber)}>{t('ext.feed.pause', 'Pause ({{type}})', { type: d.type })}{d.team ? ` · ${teams[d.team] ? teams[d.team].name : d.team}` : ''}</span></div>;
    case 'pause_end':
      return <div style={row}>{time}<span style={pill(RGB.gray)}>{t('ext.feed.unpause', 'Unpaused after {{s}}s', { s: d.duration_s })}</span></div>;
    case 'side_switch':
      return <div style={row}>{time}<span style={pill(RGB.cyan)}>{t('ext.feed.sideSwitch', 'Sides switched')}</span></div>;
    case 'halftime_start':
      return <div style={row}>{time}<span style={pill(RGB.violet)}>{t('ext.phase.halftime', 'Halftime')}</span></div>;
    case 'knife_winner':
      return <div style={row}>{time}<span style={pill(RGB.cyan)}>{t('ext.feed.knifeWinner', '{{team}} won the knife round', { team: teams[d.team] ? teams[d.team].name : d.team })}</span></div>;
    case 'knife_decision':
      return <div style={row}>{time}<span style={pill(RGB.cyan)}>{d.choice === 'switch' ? t('ext.feed.knifeSwitch', '{{team}} chose to switch sides', { team: teams[d.team] ? teams[d.team].name : d.team }) : t('ext.feed.knifeStay', '{{team}} chose to stay', { team: teams[d.team] ? teams[d.team].name : d.team })}</span></div>;
    case 'player_connect':
    case 'player_disconnect': {
      // any time of the match; the connect line has no team, so the name gets no side colour there
      const join = e.type === 'player_connect';
      const p = d.player && (join ? { ...d.player, side: null } : d.player);
      return (
        <div style={{ ...row, background: join ? 'transparent' : 'rgba(239,68,68,0.05)' }}>{time}
          <span aria-hidden="true" style={{ width: 16, height: 16, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 800, color: join ? '#22c55e' : '#ef4444', background: join ? 'rgba(34,197,94,0.14)' : 'rgba(239,68,68,0.14)' }}>{join ? '→' : '←'}</span>
          <Name p={p} /><span style={{ color: 'var(--text-secondary)' }}>{join ? t('feed.joinedShort', 'connected') : t('feed.leftShort', 'disconnected')}</span>
          {d.player && d.player.team && teams[d.player.team] && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{teams[d.player.team].name}</span>}
        </div>
      );
    }
    case 'round_restored':
      return <div style={row}>{time}<span style={pill(RGB.amber)}>{t('ext.feed.restored', 'Restored to round {{n}}', { n: d.to_round })}</span></div>;
    default:
      return null;
  }
}

/**
 * @param {Array} events   all non-voided feed events of the match, in id order
 * @param {object} match   match view (teams, maps, current_map_index)
 */
export default function ExtMatchFeed({ events, match }) {
  const { t } = useTranslation();
  const teams = match.teams;

  // map index → { name, rounds: Map(round → { events, end }), mapEvents }
  const maps = useMemo(() => {
    const out = new Map();
    const get = (mi) => {
      if (!out.has(mi)) out.set(mi, { rounds: new Map(), mapEvents: [], name: null, started: new Set() });
      return out.get(mi);
    };
    // A join / leave's own team can be missing (ext: first connect before the parser learnt it) or wrong
    // (panel events: derived from the connect line's made-up CT side): use the team of his kills / deaths
    const isConn = (e) => e.type === 'player_connect' || e.type === 'player_disconnect';
    const teamOf = new Map();
    for (const e of events) {
      if (e.type !== 'kill') continue;
      const d = e.data || {};
      for (const p of [d.attacker, d.victim]) if (p && p.steam_id && p.team && !teamOf.has(p.steam_id)) teamOf.set(p.steam_id, p.team);
    }
    const isBot = (p) => !p || String(p.steam_id || '').toUpperCase() === 'BOT' || /^(SourceTV|CSTV|GOTV)\b/i.test(String(p.name || ''));
    for (const e0 of events) {
      let e = e0;
      if (isConn(e) && isBot(e.data && e.data.player)) continue; // CSTV relays / bots are not players
      if (isConn(e) && e.data && e.data.player && teamOf.has(e.data.player.steam_id)) {
        e = { ...e, data: { ...e.data, player: { ...e.data.player, team: teamOf.get(e.data.player.steam_id) } } };
      }
      const mi = e.map_index == null ? 0 : e.map_index;
      const m = get(mi);
      if (e.type === 'map_start' && e.data && e.data.map) m.name = e.data.map;
      if (e.type === 'round_start' && e.round != null) m.started.add(e.round);
      // joins / leaves the parser puts in round 1 before it started are warmup ones
      const preRound1 = isConn(e) && e.round === 1 && !m.started.has(1);
      if (preRound1 || e.round == null || e.type === 'map_start' || e.type === 'map_end' || e.type === 'match_end' || e.type === 'knife_winner' || e.type === 'knife_decision') {
        m.mapEvents.push(preRound1 ? { ...e, round: null } : e);
        continue;
      }
      if (!m.rounds.has(e.round)) m.rounds.set(e.round, { events: [], end: null });
      const r = m.rounds.get(e.round);
      if (e.type === 'round_end') r.end = e;
      else if (e.type !== 'round_start') r.events.push(e);
    }
    for (const [mi, m] of out) if (!m.name && match.maps && match.maps[mi]) m.name = match.maps[mi].name;
    // within a round: by time, then id (events filled in later for older matches have higher ids)
    for (const m of out.values()) for (const r of m.rounds.values()) r.events.sort(byTime);
    return out;
  }, [events, match.maps]);

  const mapIdxs = [...maps.keys()].sort((a, b) => a - b);
  const [mapSel, setMapSel] = useState(null);
  const curMap = mapSel != null && maps.has(mapSel) ? mapSel : (mapIdxs.includes(match.current_map_index) ? match.current_map_index : mapIdxs[mapIdxs.length - 1]);
  const m = maps.get(curMap);
  const roundNums = m ? [...m.rounds.keys()].sort((a, b) => b - a) : [];
  // Newest first: map/match result on top, then rounds, then what happened before round 1 at the
  // bottom — knife result / pauses, and last the warmup kills & deaths (they used to sit on top of
  // every round for the whole match).
  const isEnd = (e) => e.type === 'map_end' || e.type === 'match_end';
  const isKnife = (e) => !!(e.data && e.data.knife_round); // knife-round feed: shown, never counted
  // warmup group: deathmatch kills / deaths and the joins / leaves before round 1 (mid-match ones are in their round)
  const isWarmupFrag = (e) => !isKnife(e) && (e.type === 'kill' || e.type === 'suicide' || e.type === 'player_connect' || e.type === 'player_disconnect');
  const ends = m ? m.mapEvents.filter(isEnd) : [];
  const preMatch = m ? m.mapEvents.filter(e => !isEnd(e) && !isKnife(e) && !isWarmupFrag(e)).reverse() : [];
  const knife = m ? m.mapEvents.filter(isKnife) : [];
  const warmup = m ? m.mapEvents.filter(isWarmupFrag) : [];
  // open while nothing has been played yet (so warmup is still followed live), collapsed after that
  const [warmupOpen, setWarmupOpen] = useState(null);
  const showWarmup = warmupOpen == null ? roundNums.length === 0 : warmupOpen;

  if (!events.length) return null;
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
        <div style={sectionLabel}>{t('ext.matchFeed', 'Match feed')}</div>
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
          {mapIdxs.length > 1 && mapIdxs.map(mi => (
            <button key={mi} type="button" onClick={() => setMapSel(mi)} style={{ ...pill(mi === curMap ? RGB.blue : RGB.gray), cursor: 'pointer' }}>
              {t('ext.mapN', 'Map {{n}}', { n: mi + 1 })}{maps.get(mi).name ? ` · ${maps.get(mi).name.replace(/^de_/, '')}` : ''}
            </button>
          ))}
        </div>
      </div>

      {ends.map(e => (
        <div key={e.id} style={{ padding: '0.4rem 0.5rem' }}>
          <span style={pill(RGB.green)}>
            {e.type === 'map_end' && (e.data.forfeit || !e.data.score)
              ? t('ext.feed.mapForfeit', '{{map}} forfeited - {{team}} gets the map', { map: (e.data.map || '').replace(/^de_/, ''), team: teams[e.data.winner] ? teams[e.data.winner].name : '—' })
              : e.type === 'map_end'
              ? t('ext.feed.mapEnd', '{{map}} won by {{team}} {{s1}}-{{s2}}', { map: (e.data.map || '').replace(/^de_/, ''), team: teams[e.data.winner] ? teams[e.data.winner].name : '—', s1: e.data.score.team1, s2: e.data.score.team2 })
              : t('ext.feed.matchEnd', 'Match won by {{team}}', { team: teams[e.data.winner] ? teams[e.data.winner].name : '—' })}
          </span>
        </div>
      ))}

      {roundNums.map(n => {
        const r = m.rounds.get(n);
        const end = r.end && r.end.data;
        const side = end && end.winner_side ? SIDE[end.winner_side] : null;
        const kills = r.events.filter(e => e.type === 'kill').length;
        return (
          <div key={n} style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', marginBottom: '0.4rem', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', padding: '0.45rem 0.65rem', background: side ? `rgba(${side.rgb},0.07)` : 'rgba(255,255,255,0.02)', color: 'var(--text-primary)' }}>
              <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.74rem', color: 'var(--text-secondary)', minWidth: 62 }}>{t('ext.roundN', 'Round {{n}}', { n })}</span>
              {end ? (
                <>
                  <img src={REASON_ICON[end.reason] || (end.winner_side === 'CT' ? '/killfeed/skull_t.svg' : '/killfeed/skull_ct.svg')} alt="" style={{ height: 13 }} />
                  <span style={{ fontWeight: 700, color: side ? side.color : 'var(--text-primary)', fontSize: '0.8rem' }}>{teams[end.winner] ? teams[end.winner].name : end.winner_side}</span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{t(`ext.reason.${end.reason}`, end.reason)}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.78rem' }}>{end.score.team1} – {end.score.team2}</span>
                </>
              ) : (<><span style={pill(RGB.green)}>{t('ext.inProgress', 'In progress')}</span><span style={{ flex: 1 }} /></>)}
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', minWidth: 52, textAlign: 'right' }}>{t('ext.killsN', '{{n}} kills', { n: kills })}</span>
            </div>
            <div style={{ padding: '0.2rem 0.15rem 0.35rem' }}>
              {r.events.length === 0 && <div style={{ padding: '0.3rem 0.5rem', fontSize: '0.76rem', color: 'var(--text-muted)' }}>{t('ext.noEventsYet', 'Nothing yet.')}</div>}
              {r.events.map(e => <Row key={e.id} e={e} teams={teams} />)}
            </div>
          </div>
        );
      })}

      {/* before round 1 (oldest): knife result, pauses … */}
      {preMatch.map(e => <Row key={e.id} e={e} teams={teams} />)}

      {/* knife round: its kills are shown (not counted in any stat), older than the knife result above */}
      {knife.length > 0 && (
        <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', margin: '0.4rem 0', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', padding: '0.45rem 0.65rem', background: `rgba(${RGB.cyan},0.06)`, color: 'var(--text-primary)' }}>
            <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.74rem', color: 'var(--text-secondary)' }}>{t('matchDetail.knifeRound', 'Knife Round')}</span>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{t('ext.feed.warmupNote', 'not counted in stats')}</span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{t('ext.killsN', '{{n}} kills', { n: knife.filter(e => e.type === 'kill').length })}</span>
          </div>
          <div style={{ padding: '0.2rem 0.15rem 0.35rem' }}>
            {knife.map(e => <Row key={e.id} e={e} teams={teams} />)}
          </div>
        </div>
      )}

      {/* … and the warmup kills & deaths, last and collapsible */}
      {warmup.length > 0 && (
        <div style={{ border: '1px dashed var(--border-subtle)', borderRadius: 'var(--radius-md)', marginTop: '0.4rem', overflow: 'hidden', opacity: 0.85 }}>
          <button type="button" onClick={() => setWarmupOpen(!showWarmup)} aria-expanded={showWarmup}
            style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', width: '100%', padding: '0.45rem 0.65rem', background: 'rgba(255,255,255,0.015)', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', textAlign: 'left' }}>
            <span style={{ fontSize: '0.7rem', width: 10, display: 'inline-block', transform: showWarmup ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</span>
            <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.74rem' }}>{t('ext.phase.warmup', 'Warmup')}</span>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{t('ext.feed.warmupNote', 'not counted in stats')}</span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{t('ext.killsN', '{{n}} kills', { n: warmup.filter(e => e.type === 'kill').length })}</span>
          </button>
          {showWarmup && (
            <div style={{ padding: '0.2rem 0.15rem 0.35rem', maxHeight: 360, overflowY: 'auto' }}>
              {warmup.map(e => <Row key={e.id} e={e} teams={teams} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
